import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  ecToTds, tdsToEc, toBase, fromBase, fmt, UNITS,
  SpecCard, SpecValue,
} from "./_shared";

const FACTORS = [
  { value: "0.50", label: "0.50 — soft / chloride-dominant" },
  { value: "0.64", label: "0.64 — natural water (default)" },
  { value: "0.70", label: "0.70 — brackish / sulphate-rich" },
];

function tdsStatus(tds_mgL) {
  if (tds_mgL == null) return [undefined, null];
  if (tds_mgL <= 600)  return ["ok",    "Within the SANS 241 ideal aesthetic range (≤ 600 mg/L)."];
  if (tds_mgL <= 1200) return ["warn",  "Above 600 mg/L — palatable but moderate. SANS 241 allows up to 1 200 mg/L."];
  return ["alert", "Above the SANS 241 aesthetic limit of 1 200 mg/L. Polishing or RO recommended."];
}

export default function TDSConverter() {
  const [mode, setMode] = useState("ec-to-tds");
  const [factor, setFactor] = useState("0.64");

  const [ecVal, setEcVal]   = useState(800);
  const [ecUnit, setEcUnit] = useState("uScm");
  const [tdsVal, setTdsVal] = useState(500);
  const [tdsUnit, setTdsUnit] = useState("mgL");

  const f = parseFloat(factor);
  const ec_uScm = toBase("conductivity", ecVal, ecUnit);
  const tds_mgL = toBase("tds", tdsVal, tdsUnit);

  const tdsOut = useMemo(() => mode === "ec-to-tds" ? ecToTds(ec_uScm, f) : null, [mode, ec_uScm, f]);
  const ecOut  = useMemo(() => mode === "tds-to-ec" ? tdsToEc(tds_mgL, f) : null, [mode, tds_mgL, f]);

  const ec_show = mode === "ec-to-tds" ? ec_uScm : ecOut;
  const tds_show = mode === "ec-to-tds" ? tdsOut : tds_mgL;
  const resistivity = ec_show && ec_show > 0 ? 1000 / ec_show : null;

  const [status, note] = tdsStatus(tds_show);

  return (
    <ToolShell
      slug="tds-ec"
      title="TDS ↔ EC converter"
      oneLiner="Conductivity (µS/cm) and total dissolved solids (mg/L) translate through a factor that depends on the dominant salt mix."
      onExport={() => ({
        inputs: [
          { label: "Direction", value: mode === "ec-to-tds" ? "EC → TDS" : "TDS → EC" },
          { label: "Conversion factor", value: factor },
          mode === "ec-to-tds"
            ? { label: "Conductivity (EC)", value: ecVal, unit: UNITS.conductivity.labels[ecUnit] }
            : { label: "Total dissolved solids", value: tdsVal, unit: UNITS.tds.labels[tdsUnit] },
        ],
        results: [
          { label: "TDS", value: fmt(tds_show), unit: "mg/L",
            alts: tds_show !== null ? [{ value: fmt(fromBase("tds", tds_show, "gL"), 3), unit: "g/L" }] : null,
            status, note },
          { label: "EC", value: fmt(ec_show), unit: "µS/cm",
            alts: ec_show !== null ? [{ value: fmt(fromBase("conductivity", ec_show, "mScm"), 3), unit: "mS/cm" }] : null },
          { label: "Resistivity", value: fmt(resistivity), unit: "kΩ·cm" },
        ],
        notes: ["Formula: TDS (mg/L) = factor × EC (µS/cm).  Resistivity (kΩ·cm) = 1000 / EC."],
        spec: tds_show !== null && ec_show !== null ? {
          title: "Water quality reading",
          body: `Conductivity of <strong>${fmt(ec_show, 0)} µS/cm</strong> at factor <strong>${factor}</strong> corresponds to <strong>${fmt(tds_show, 0)} mg/L</strong> TDS and a resistivity of <strong>${fmt(resistivity, 2)} kΩ·cm</strong>.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Conversion direction">
          <Select value={mode} onChange={setMode} options={[
            { value: "ec-to-tds", label: "EC → TDS" },
            { value: "tds-to-ec", label: "TDS → EC" },
          ]} testid="tds-mode" />
        </Field>
        <Field label="Conversion factor" hint="Higher for hard / brackish water, lower for soft.">
          <Select value={factor} onChange={setFactor} options={FACTORS} testid="tds-factor" />
        </Field>

        {mode === "ec-to-tds" ? (
          <Field label="Conductivity (EC)" span={2}>
            <UnitNumber dim="conductivity" value={ecVal} onChange={setEcVal} unit={ecUnit} onUnitChange={setEcUnit}
              units={["uScm", "mScm"]} testid="tds-ec-input" />
          </Field>
        ) : (
          <Field label="Total dissolved solids" span={2}>
            <UnitNumber dim="tds" value={tdsVal} onChange={setTdsVal} unit={tdsUnit} onUnitChange={setTdsUnit}
              units={["mgL", "ppm", "gL"]} testid="tds-tds-input" />
          </Field>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Result
          label="TDS"
          primary={{ value: fmt(tds_show), unit: "mg/L" }}
          alts={tds_show !== null ? [
            { value: fmt(fromBase("tds", tds_show, "gL"), 3), unit: "g/L" },
          ] : null}
          status={status}
          note={note}
          testid="tds-result-tds"
        />
        <Result
          label="EC"
          primary={{ value: fmt(ec_show), unit: "µS/cm" }}
          alts={ec_show !== null ? [
            { value: fmt(fromBase("conductivity", ec_show, "mScm"), 3), unit: "mS/cm" },
          ] : null}
          testid="tds-result-ec"
        />
        <Result
          label="Resistivity"
          primary={{ value: fmt(resistivity), unit: "kΩ·cm" }}
          testid="tds-result-res"
        />
      </div>

      {tds_show !== null && ec_show !== null && (
        <SpecCard
          eyebrow="Water quality reading"
          testid="tds-spec-card"
          footer={note}
        >
          Conductivity of <SpecValue>{fmt(ec_show, 0)} µS/cm</SpecValue> at factor{" "}
          <SpecValue>{factor}</SpecValue> corresponds to{" "}
          <SpecValue>{fmt(tds_show, 0)} mg/L</SpecValue> TDS and a resistivity of{" "}
          <SpecValue>{fmt(resistivity, 2)} kΩ·cm</SpecValue>.
        </SpecCard>
      )}
    </ToolShell>
  );
}
